const LOAD_TIMEOUT = 5000;
const TOKEN_TIMEOUT = 10000;
const POLL_INTERVAL = 100;

export const RECAPTCHA_UNAVAILABLE = 'reCAPTCHA not loaded';

export function loadRecaptchaApi(siteKey) {
  if (window.recaptchaApiRequested) {
    return;
  }
  window.recaptchaApiRequested = true;

  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
  script.async = true;
  script.onerror = function reportRecaptchaError() {
    window.recaptchaApiRequested = false;
    if (window.Sentry) {
      window.Sentry.captureException(
        new Error('reCAPTCHA api.js failed to load'),
        { extra: { src: script.src } },
      );
    }
  };
  document.head.appendChild(script);
}

function whenRecaptchaReady(siteKey) {
  if (typeof grecaptcha !== 'undefined') {
    return Promise.resolve();
  }

  // The idle/focusin load may not have finished yet. Wait for it rather than
  // sending a blank token, which the server rejects (lost quotation).
  loadRecaptchaApi(siteKey);

  return new Promise((resolve, reject) => {
    let waited = 0;

    const poll = setInterval(() => {
      if (typeof grecaptcha !== 'undefined') {
        clearInterval(poll);
        resolve();
      } else if ((waited += POLL_INTERVAL) >= LOAD_TIMEOUT) {
        clearInterval(poll);
        reject(new Error(RECAPTCHA_UNAVAILABLE));
      }
    }, POLL_INTERVAL);
  });
}

function execute(siteKey, action) {
  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(siteKey, { action }).then(resolve, reject);
    });
  });
}

export function requestRecaptchaToken(input) {
  const siteKey = input?.dataset?.recaptchaSiteKey;
  const action = input?.dataset?.recaptchaAction;

  if (!siteKey || !action) {
    return Promise.resolve(null);
  }

  // grecaptcha.execute never settles when Google refuses the site key, so the
  // load timeout alone would leave the caller waiting forever.
  return Promise.race([
    whenRecaptchaReady(siteKey).then(() => execute(siteKey, action)),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(RECAPTCHA_UNAVAILABLE)), TOKEN_TIMEOUT);
    }),
  ]);
}
